import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search, Edit2, Trash2, Layers, X, CheckCircle2 } from 'lucide-react';

// Admin materials catalogue — add/edit/remove materials WITH civil sizes/specs.
// Talks to /materials (GET open; POST/PUT/DELETE require admin — enforced server-side).
export default function MaterialsManager({ api, showToast }) {
    const EMPTY = { category: '', name: '', spec: '', size: '', unit: '', hsn: '' };

    const [items, setItems]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const [catFilter, setCatFilter] = useState('All');
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId]     = useState(null);
    const [form, setForm]         = useState(EMPTY);
    const [saving, setSaving]     = useState(false);

    const load = async () => {
        setLoading(true);
        try { setItems(await api('materials') || []); }
        catch (e) { showToast && showToast('Could not load materials'); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const categories = useMemo(
        () => [...new Set(items.map(m => m.category).filter(Boolean))].sort(),
        [items]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter(m =>
            (catFilter === 'All' || m.category === catFilter) &&
            (!q || [m.category, m.name, m.spec, m.size, m.unit]
                .some(v => (v || '').toLowerCase().includes(q)))
        );
    }, [items, search, catFilter]);

    const openAdd  = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
    const openEdit = (m) => {
        setForm({ category: m.category || '', name: m.name || '', spec: m.spec || '',
                  size: m.size || '', unit: m.unit || '', hsn: m.hsn || '' });
        setEditId(m.id); setShowForm(true);
    };
    const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.category.trim() || !form.name.trim()) {
            showToast && showToast('Category and name are required'); return;
        }
        setSaving(true);
        try {
            if (editId) await api(`materials/${editId}`, 'PUT', form);
            else        await api('materials', 'POST', form);
            setShowForm(false); setForm(EMPTY); setEditId(null);
            await load();
            showToast && showToast(editId ? 'Material updated' : 'Material added');
        } catch (e) {
            showToast && showToast('Save failed — you may need admin rights');
        }
        setSaving(false);
    };

    const remove = async (m) => {
        if (!window.confirm(`Remove "${m.name}${m.size ? ' · ' + m.size : ''}"? A backup is kept on the NAS.`)) return;
        try { await api(`materials/${m.id}`, 'DELETE'); await load(); showToast && showToast('Material removed'); }
        catch (e) { showToast && showToast('Delete failed'); }
    };

    const inputStyle = {
        width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0',
        borderRadius: '8px', fontSize: '13px', background: '#fff', color: 'var(--slate-900)'
    };
    const th = { textAlign: 'left', padding: '10px 12px', fontSize: '11px', textTransform: 'uppercase',
                 letterSpacing: '0.04em', color: 'var(--slate-500)', borderBottom: '1px solid #eef2f7' };
    const td = { padding: '10px 12px', fontSize: '13px', color: 'var(--slate-800)', borderBottom: '1px solid #f5f7fa' };

    return (
        <div className="card" style={{ padding: '18px' }}>
            {/* toolbar */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div style={{ position: 'relative', flex: '1 1 220px' }}>
                    <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--slate-400)' }} />
                    <input placeholder="Search material, grade, size…" value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ ...inputStyle, paddingLeft: '30px' }} />
                </div>
                <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: '150px' }}>
                    <option value="All">All categories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Material</button>
            </div>

            <div style={{ fontSize: '12px', color: 'var(--slate-400)', marginBottom: '8px' }}>
                {loading ? 'Loading…' : `${filtered.length} material${filtered.length === 1 ? '' : 's'}${catFilter !== 'All' ? ' in ' + catFilter : ''}`}
            </div>

            {/* table */}
            <div style={{ overflowX: 'auto', border: '1px solid #eef2f7', borderRadius: '10px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                    <thead>
                        <tr>
                            <th style={th}>Category</th>
                            <th style={th}>Material</th>
                            <th style={th}>Grade / Spec</th>
                            <th style={th}>Size</th>
                            <th style={th}>Unit</th>
                            <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(m => (
                            <tr key={m.id}>
                                <td style={td}><span style={{ color: 'var(--slate-500)' }}>{m.category}</span></td>
                                <td style={{ ...td, fontWeight: 600, color: 'var(--slate-900)' }}>{m.name}</td>
                                <td style={td}>{m.spec || '—'}</td>
                                <td style={td}>{m.size
                                    ? <span style={{ fontWeight: 600, color: '#1743e6' }}>{m.size}</span>
                                    : '—'}</td>
                                <td style={td}>{m.unit || '—'}</td>
                                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    <button className="btn btn-light" style={{ fontSize: '11px', padding: '5px 8px', marginRight: '6px' }} onClick={() => openEdit(m)}><Edit2 size={12} /></button>
                                    <button className="btn btn-danger" style={{ fontSize: '11px', padding: '5px 8px' }} onClick={() => remove(m)}><Trash2 size={12} /></button>
                                </td>
                            </tr>
                        ))}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: 'var(--slate-400)', padding: '28px' }}>
                                <Layers size={22} style={{ opacity: 0.4, display: 'block', margin: '0 auto 8px' }} />
                                No materials found. Click “Add Material” to create one.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* add / edit form */}
            {showForm && (
                <div className="modal-overlay" onClick={() => setShowForm(false)}>
                    <div className="modal-box" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>{editId ? 'Edit Material' : 'Add Material'}</h3>
                            <button className="btn btn-ghost" style={{ padding: '6px' }} onClick={() => setShowForm(false)}><X size={16} /></button>
                        </div>
                        <div style={{ display: 'grid', gap: '12px', padding: '4px 2px 8px' }}>
                            <div>
                                <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Category *</label>
                                <input list="mat-cats" value={form.category} onChange={e => upd('category', e.target.value)} placeholder="e.g. Steel, Cement, Plumbing" style={inputStyle} />
                                <datalist id="mat-cats">{categories.map(c => <option key={c} value={c} />)}</datalist>
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Material name *</label>
                                <input value={form.name} onChange={e => upd('name', e.target.value)} placeholder="e.g. TMT Steel Bar" style={inputStyle} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Grade / Spec</label>
                                    <input value={form.spec} onChange={e => upd('spec', e.target.value)} placeholder="e.g. Fe500D" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Size</label>
                                    <input value={form.size} onChange={e => upd('size', e.target.value)} placeholder="e.g. 16 mm" style={inputStyle} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Unit</label>
                                    <input value={form.unit} onChange={e => upd('unit', e.target.value)} placeholder="e.g. kg, bag, nos" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>HSN (optional)</label>
                                    <input value={form.hsn} onChange={e => upd('hsn', e.target.value)} placeholder="e.g. 7214" style={inputStyle} />
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button className="btn btn-light" style={{ flex: 1 }} onClick={() => setShowForm(false)}>Cancel</button>
                            <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving}>
                                <CheckCircle2 size={14} /> {saving ? 'Saving…' : (editId ? 'Save changes' : 'Add material')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
